import { useState, useEffect, useCallback } from "react";
import { useGame } from "@/contexts/GameContext"; // Upewnij się, że ścieżka jest poprawna
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Star, Check, SkipForward, ArrowLeft, X } from "lucide-react"; // Dodano ikony nawigacyjne
import { toast } from "sonner";
import { getDifficultyStyle } from "@/lib/constants"; // Upewnij się, że ścieżka jest poprawna
import { cn } from "@/lib/utils";

export default function QuestionScreen() {
  const { state, dispatch } = useGame();
  const [timer, setTimer] = useState(60); // Domyślny czas
  const [timerActive, setTimerActive] = useState(false);

  const currentPlayer = state.players[state.currentPlayerIndex];

  // Jeśli nie ma graczy (np. po resecie), wróć do startu
  if (!currentPlayer) {
    dispatch({ type: "SET_SCREEN", payload: "start" });
    return null; // Zwróć null, aby uniknąć błędów renderowania
  }

  const currentQuestion = state.currentQuestion;
  const isQuestion = currentQuestion?.type === "pytanie";

  // Określenie aktywnego gracza (tego, który wykonuje zadanie)
  const activePlayer =
    state.selectedPlayerIndex !== null &&
    state.players[state.selectedPlayerIndex]
      ? state.players[state.selectedPlayerIndex]
      : state.players[state.currentPlayerIndex];

  const commonDispatchActions = useCallback(() => {
    dispatch({ type: "SET_CURRENT_QUESTION", payload: null });
    dispatch({ type: "SHOW_QUESTION_DIALOG", payload: false });
    dispatch({ type: "NEXT_TURN" });
    setTimerActive(false);
  }, [dispatch]); // dispatch jest stabilny

  const handleComplete = useCallback(
    (completed) => {
      // payload: { completed } jest poprawny dla akcji COMPLETE_TASK
      dispatch({ type: "COMPLETE_TASK", payload: { completed } });
      commonDispatchActions();
    },
    [dispatch, commonDispatchActions] // commonDispatchActions, jeśli zdefiniowane w useCallback, powinno być w zależnościach
  );

  const handleSkip = useCallback(() => {
    // SKIP_TASK nie potrzebuje payload, bo zawsze oznacza użycie 'skip'
    dispatch({ type: "SKIP_TASK" });
    commonDispatchActions();
  }, [dispatch, commonDispatchActions]);

  // Efekt dla Timera
  useEffect(() => {
    // Uruchom timer tylko jeśli jest pytanie i ekran jest aktywny
    if (!currentQuestion) {
      setTimerActive(false);
      return;
    }

    setTimer(60); // Resetuj timer przy każdym nowym pytaniu
    setTimerActive(true);

    return () => setTimerActive(false); // Czyść przy odmontowaniu lub zmianie pytania
  }, [currentQuestion]); // Zależność tylko od currentQuestion

  useEffect(() => {
    if (!timerActive) return;

    if (timer === 0) {
      toast.warning("Czas na zadanie minął!", { duration: 2000 });
      handleComplete(false); // Automatyczne poddanie się
      return;
    }

    const intervalId = setInterval(() => {
      setTimer((t) => Math.max(0, t - 1)); // Upewnij się, że timer nie idzie poniżej 0
    }, 1000);

    return () => clearInterval(intervalId);
  }, [timerActive, timer, handleComplete]);

  if (!currentQuestion || !activePlayer) {
    // Jeśli nie ma pytania lub gracza, nie renderuj nic (GameScreen zdecyduje co dalej)
    // Można by też pokazać jakiś fallback lub loader, ale GameScreen powinien to obsłużyć
    return null;
  }

  const difficultyStyle = getDifficultyStyle(currentQuestion.difficulty);
  const canSkip = activePlayer.skipsUsed < 2; // Załóżmy limit 2 pominięć

  // Funkcja "powrotu" - traktowana jak poddanie się, ale bez komunikatu o końcu czasu
  const handleGoBack = () => {
    toast.info("Anulowano zadanie.", { duration: 2000 });
    handleComplete(false); // Traktuj jak poddanie się
  };

  return (
    <>
      {/* Header - Prosty header dla ekranu pytania */}
      <header className="w-full flex items-center justify-between mb-4">
        <Button
          onClick={handleGoBack}
          variant="ghost"
          size="icon"
          className="rounded-full"
        >
          <ArrowLeft className="size-5" />
        </Button>
        <div className="text-center">
          <div className="text-sm text-white/60">Tura gracza:</div>
          <div className="flex items-center justify-center gap-2">
            <span className="text-lg font-bold">{activePlayer?.name}</span>
          </div>
        </div>
        <div className="w-9 h-9" /> {/* Spacer */}
      </header>

      <main className="flex-1 flex flex-col items-center w-full text-center space-y-6">
        {/* Info o graczu i tagi */}
        <div className="w-full">
          <div className="flex flex-wrap items-center justify-center gap-2 mb-4">
            <Badge
              variant="outline"
              className={cn(
                isQuestion
                  ? "border-blue-500 text-blue-500"
                  : "border-red-500 text-red-500"
              )}
            >
              {isQuestion ? "Pytanie" : "Wyzwanie"}
            </Badge>
            <Badge
              variant="outline"
              className={cn(
                `${difficultyStyle.border} ${difficultyStyle.text}`
              )}
            >
              <div className="flex items-center gap-1 py-0.5">
                {Array.from({ length: currentQuestion.difficulty }).map(
                  (_, i) => (
                    <Star key={i} className="size-3 fill-current" />
                  )
                )}
              </div>
            </Badge>
            {currentQuestion.nsfw && <Badge variant="destructive">18+</Badge>}
          </div>
        </div>

        {/* Ikona i Tekst Pytania/Wyzwania */}
        <div className="w-16 h-16 md:w-20 md:h-20 mb-2 md:mb-4 flex items-center justify-center text-5xl md:text-6xl">
          {isQuestion ? "🤔" : "😈"}
        </div>
        <p className="text-2xl sm:text-3xl md:text-4xl font-bold leading-tight px-2">
          {currentQuestion.text}
        </p>

        {/* Timer */}
        <div
          className={cn(
            "w-24 h-24 md:w-28 md:h-28 mt-4 rounded-full border-4 flex flex-col items-center justify-center shadow-lg transition-colors",
            timer <= 10
              ? "border-destructive text-destructive animate-pulse"
              : "border-primary text-primary"
          )}
        >
          <div className="text-4xl md:text-5xl font-bold">{timer}</div>
          <div className="text-xs uppercase text-muted-foreground">Sekund</div>
        </div>
      </main>

      {/* Stopka z przyciskami akcji */}
      <footer
        className={
          "w-full bg-background py-4 fixed bottom-0 left-0 right-0 z-20 " + // Dodano left-0 i z-20
          "border-t border-border " + // Subtelna linia oddzielająca na górze
          "shadow-[0_-6px_12px_-3px_rgba(0,0,0,0.2),_0_-4px_8px_-4px_rgba(0,0,0,0.05)] " + // Cień na górze
          "dark:shadow-[0_-6px_12px_-3px_rgba(0,0,0,0.8),_0_-4px_8px_-4px_rgba(0,0,0,0.15)]" // Cień dla trybu ciemnego
        }
      >
        <div className="max-w-xl mx-auto grid grid-cols-2 gap-4 container">
          <Button
            onClick={() => handleComplete(true)}
            size="lg"
            className="bg-green-600 hover:bg-green-700 text-white font-bold"
          >
            <Check className="size-5" /> Zrobione!
          </Button>
          <Button
            onClick={() => handleComplete(false)}
            size="lg"
            variant="destructive"
            className="font-bold"
          >
            <X className="size-5" /> Poddaję się
          </Button>

          <Button
            onClick={handleSkip}
            variant="outline"
            size="lg"
            className="col-span-2 border-dashed hover:border-solid"
            disabled={!canSkip}
          >
            <SkipForward className="size-5" />
            Pomiń ({2 - activePlayer.skipsUsed} pozostało)
          </Button>
        </div>
      </footer>
    </>
  );
}
